import React, { useEffect, useState } from "react";
import { Redirect, Route, Switch } from 'react-router-dom';
import '../../styles/internal/internal-styles.css';
import {Box, CircularProgress, Grid} from '@mui/material';
import { showNotificationBar } from "../../actions/error-pages/NotificationBarActions";
import { getAdminAccessLevel, getCurrentUser, isAdmin, signUserOut } from "../../actions/profile/UserActions";
import Colors from "../../constants/Colors";
import ErrorPanel from "../error-pages/ErrorPanel";
import GenericNotFound from "../error-pages/GenericNotFound";
import DashboardComponent from "./dashboard/DashboardComponent";
import InternalHeader from "./InternalHeader";
import InternalSiteRouter from "./InternalSiteRouter";
import { SESSION_REDIRECT_KEY, setSessionProperty } from "../../utilities/SessionUtilities";
import InternalFooter from "./InternalFooter";
import { getAllSitesForUser } from "../../actions/internal/SiteActions";
import UserAccountDetails from "./account/UserAccountDetails";
import AdminRouter from "./admin/AdminRouter";
import { StyledLoadingSection } from "../common/StyledProgressCircle";
import { getAllSites } from "../../selectors/SiteSelectors";
import UserReviewPoliciesPage from "./account/UserReviewPoliciesPage";
import { getUserObject } from "../../selectors/UserSelectors";
import { validatePolicyAcceptance } from "../../actions/internal/LegalActions";
import UserAccountSetupPage from "./account/UserAccountSetupPage";
import ReactGA from "react-ga4";
import AnalyticsTracker from "../../utilities/AnalyticsTracker";

const InternalRouter = ({ history, location }) => {
    const [ isLoading, setIsLoading ] = useState(true);
    const [ error, setError ] = useState(false);

    const sites = getAllSites();

    useEffect(() => {
        loadSession();
    }, [])

    const loadSession = async () => {
        setIsLoading(true)
        try {
            const user = await getCurrentUser();
            if (!user) {
                setSessionProperty(SESSION_REDIRECT_KEY, location.pathname)
                history.replace('/login');
                return;
            }
            ReactGA.set({ user_id: user.user_id })
            await getAllSitesForUser(user.user_id)
            const policyCheck = validatePolicies();
            if (policyCheck) {
                history.replace({
                    pathname: '/account/review'
                });
                return;
            }
            if (!isUserSetup()) {
                history.replace({
                    pathname: '/account/setup'
                });
                return;
            }
        } catch(ex) {
            if (ex.code === 'UserNotFoundException') {
                signUserOut();
                history.replace('/login');
                return;
            }
            const error = await ex;
            console.error(error);
            setError('Something went wrong!');
            showNotificationBar('error', 'Oh no! A problem occurred, please try again later.');
            history.replace('/login');
        } finally {
            setIsLoading(false)
        }

    }

    const validatePolicies = () => {
        const user = getUserObject();
        const sites = getAllSites();

        const policies = validatePolicyAcceptance(user, sites);
        if (policies.length > 0) {
            const redirect = location.pathname;
            return { redirect, policies }
        }
    }

    const isUserSetup = () => {
        const user = getUserObject();
        if (!user || !user.metadata || !user.metadata.dashboard_setup_timestamp) return false;
        return true;
    }

    const loadingContent = (
        <StyledLoadingSection />
    )

    const getRedirectForSiteAccess = (siteIdFromUrl) => {
        const user = getUserObject();
      
        // If policies aren't accepted, redirect to review page
        const policyCheck = validatePolicies();
        if (policyCheck) {
          return (
            <Redirect
              to={{
                pathname: '/account/review',
                state: { policyCheck }
              }}
            />
          );
        }

        // If user isn't setup, redirect to setup page
        if (!user || !user.metadata || !user.metadata.dashboard_setup_timestamp) {
            return <Redirect to="/account/setup" />;
        }
      
        // If no sites, send to setup page
        if (!sites || sites.length === 0) {
          return <Redirect to="/account/setup" />;
        }
      
        // If site ID is given, make sure it's valid
        const siteMatch = sites.find((s) => s.site_id === siteIdFromUrl);
        if (!siteMatch && siteIdFromUrl && !isAdmin()) {
          return <Redirect to={`/dashboard/site/${sites[0].site_id}`} />;
        }
      
        return null; // No issues, allow access
      };

    const mainContent = !isLoading && !error ?
        <Switch>
            <Route
                exact
                path="/dashboard"
                render={() => {
                    const redirect = getRedirectForSiteAccess(null);
                    const access = getAdminAccessLevel();
                    if (access > 0) {
                        return redirect || <Redirect to={`/admin`} />;
                    }
                    return redirect || <Redirect to={`/dashboard/site/${sites[0].site_id}`} />;
                }}
            />

            <Route
                path="/dashboard/site/:site_id"
                render={(routeProps) => {
                    const { site_id } = routeProps.match.params;
                    const redirect = getRedirectForSiteAccess(site_id);
                    return redirect || <InternalSiteRouter {...routeProps} />;
                }}
            />

            {/* View Account Routes */}
            <Route exact path="/account" render={routeProps => <UserAccountDetails {...routeProps} />} />
            <Route exact path="/account/me" render={routeProps => <UserAccountDetails {...routeProps} />} />

            {/* Review Info & Policies Routes */}
            <Route path="/account/review" render={routeProps => <UserReviewPoliciesPage {...routeProps} />} />

            {/* Review Info & Policies Routes */}
            <Route path="/account/setup" render={routeProps => <UserAccountSetupPage {...routeProps} />} />

            <Route path="/admin" render={routeProps => <AdminRouter {...routeProps} />} />
            <Route path="*" render={routeProps => <GenericNotFound {...routeProps} />} />
        </Switch>
        :
        <ErrorPanel
            message={"Oh no! Something went wrong. Please check your internet connection and try again."}
        />;
    return (
        <React.Fragment>
            <AnalyticsTracker />
            <InternalHeader parentIsLoading={isLoading} />
            {isLoading ? loadingContent : mainContent}
        </React.Fragment>
    );
}

/*
 <React.Fragment>
            <InternalHeader />
                <Box sx={{ minHeight: '80vh', px: 2, py: 3 }}>
                    <Box
                        sx={{
                        maxWidth: '1140px',
                        margin: '0 auto',
                        width: '100%',
                        }}
                    >
                        {isLoading ? loadingContent : mainContent}
                    </Box>
                </Box>

            <InternalFooter />
        </React.Fragment>
*/

export default InternalRouter;
